/**
 * Mirrors the client-facing half of `backend/src/game/battleground/`.
 *
 * Two different wires feed this file and they must not be confused:
 *
 * - The **Colyseus room's synced schema** (`BattlegroundRoomState` in
 *   `battleground.room.ts`) arrives over the `/bg` WebSocket, decoded by
 *   reflection (see `battleground-state.ts` for why no schema classes are
 *   shared). `RoomSnapshot` below is the plain-object projection of it.
 * - The **Socket.io orchestration events** (`battleground:pvpConcluded`)
 *   arrive over `/ws` like every other game event. The outcome of a duel comes
 *   from there, NOT from the room — the room is already disposed by the time
 *   the result is known to the rest of the game.
 */

/** Which weapon the hotbar's first three keys put in your hands. Mirrors `BattlegroundWeaponSlot`. */
export type WeaponSlotId = 'UNARMED' | 'PRIMARY' | 'SECONDARY';

/** `Vec3State` — the room syncs full vectors; only x/z are meaningful (the sim is top-down). */
export interface RoomVec3 { x: number; y: number; z: number; }

/** One duelist, from the room's `players` MapSchema. Keyed by `userId` server-side. */
export interface RoomPlayer {
  userId: string;
  position: RoomVec3;
  facing: RoomVec3;
  hp: number;
  /**
   * Current armour.
   *
   * Phase 3 added this to the room schema — `PlayerState` carried
   * `{userId, position, facing, hp}` only, so an armour bar had no source at
   * all. Optional here because a room from an older server simply omits it and
   * the bar then renders HP alone rather than throwing.
   */
  armor?: number;
  maxHp?: number;
  maxArmor?: number;
  /**
   * ─── What this fighter is actually holding, right now ────────────────────
   *
   * The equipment sheet says what they LAUNCHED with. The hotbar lets them
   * switch mid-duel, so from the first keypress the sheet is history and only
   * the room knows the truth. All five fields are optional because an older
   * server simply omits them and every reader below falls back to the sheet.
   *
   * `arcLength`/`arcWidth` matter most: `arena-view.ts` draws the line of shot
   * from them, and an arc drawn from the sheet after a swap would promise
   * reach the server will not honour — the exact drift `aim-arc.ts`'s header
   * forbids.
   */
  /** Which hotbar cell is lit. @see `PlayerState.weaponSlot` for why this is synced and not inferred. */
  weaponSlot?: WeaponSlotId;
  weaponKey?: string;
  weaponKind?: string;
  weaponAmmoType?: string;
  arcLength?: number;
  arcWidth?: number;
  /**
   * Rounds left. `-1` means this weapon has no magazine concept at all (melee,
   * bare hands, an unfed calibre) and must render as nothing rather than as
   * "empty" — see `PlayerState.ammoRemaining` server-side.
   */
  ammoRemaining?: number;
  /**
   * Ms left on this fighter's heal channel, 0 when not healing.
   *
   * Synced for BOTH duelists on purpose — see `PlayerState.healingRemaining`.
   * An opponent standing still for eight seconds is the opening those eight
   * seconds are supposed to buy you, and it is only an opening if you can see
   * it.
   */
  healingRemaining?: number;
  /**
   * ─── What this fighter's body looks like ─────────────────────────────────
   *
   * Until these existed, `arena-view.ts` drew every opponent as
   * `/models/char-2.glb` — its own comment called that "a WIRE gap, not a
   * choice", because nothing carried a preset id across. This is the wire.
   *
   * All optional, like the weapon block above: an older server omits them and
   * the view falls back to the default body rather than failing to render a
   * duel.
   */
  presetId?: string;
  colorScheme?: number;
  /**
   * Worn item keys. The room syncs them comma-joined (one string beats an
   * ArraySchema for a list written once and never changed); this is the split
   * form, in the same shape `WorldObject.equippedKeys` uses, so the renderer
   * resolves them through the garment/torso tables it already has.
   */
  equippedKeys?: string[];
}

/** One enemy, from the room's `enemies` ArraySchema. PvE only — a duel's array is empty. */
export interface RoomEnemy {
  id: string;
  position: RoomVec3;
  hp: number;
  armor?: number;
  maxHp?: number;
}

/**
 * The room's synced state, flattened to plain objects.
 *
 * Deliberately a snapshot rather than the live Colyseus `Schema` instance: the
 * decoder mutates its objects in place, so handing the raw state to the
 * renderer would make every `===` comparison and every store subscriber
 * useless (the object is always the same reference, always already updated).
 */
export interface RoomSnapshot {
  /** Ms until the pre-duel freeze ends. 0 once live. Server's clock, not ours. */
  freezeRemaining: number;
  alerted: boolean;
  enemies: RoomEnemy[];
  players: RoomPlayer[];
  exfilProgress: number;
  timeRemaining: number;
}

export const EMPTY_SNAPSHOT: RoomSnapshot = {
  freezeRemaining: 0,
  alerted: false,
  enemies: [],
  players: [],
  exfilProgress: 0,
  timeRemaining: 0,
};

/** Room -> client, on every resolved `attack`. Mirrors `AttackResultBroadcast`. */
export interface AttackResult {
  attackerId: string;
  targetId?: string;
  hit: boolean;
  crit: boolean;
  damage: number;
}

/**
 * A heal worth showing as a floating `+XX`.
 *
 * ─── Two sources, and they answer different questions ───────────────────────
 * The **room's `healResult`** is the in-fight one. It fires when a fighter
 * burns a consumable off the hotbar (`handleUseItem`) — the day this docstring
 * was written that path did not exist and the listener was inert; it does now.
 *
 * The **health sheet delta** is the other: `health:updated` arrives on the game
 * socket whenever HP changes, and an increase between two pushes is a heal.
 * That is the only source for passive regen, which is lazy and has no event of
 * its own, and it works in the settlement as well as in a duel.
 *
 * `amount` is the HP actually restored, so a kit used at full health is 0 and
 * shows nothing.
 */
export interface HealResult {
  targetId: string;
  amount: number;
  source: 'kit' | 'regen';
}

/**
 * Room -> us alone: the trigger was pulled on an empty weapon.
 *
 * Mirrors `OutOfAmmoBroadcast`. Sent only to the shooter, so receiving one
 * always means *you* are dry — never the opponent.
 */
export interface OutOfAmmo { itemKey: string | null; ammoType: string | null; }

/** Room -> us alone: a heal began. The countdown itself rides `RoomPlayer.healingRemaining`. */
export interface HealStarted {
  userItemId: string;
  itemKey: string;
  itemName: string;
  durationMs: number;
}

/** Room -> us alone: the channel was cut short. The item is NOT spent. */
export interface HealCancelled { reason: 'attack' | 'swap'; }

/** Room -> us alone, after a hotbar consumable resolved. Mirrors `ItemUsedBroadcast`. */
export interface ItemUsed { itemKey: string; itemName: string; userItemId: string; }

/** Socket.io `battleground:pvpConcluded`. The ONLY place a duel's outcome reaches the client. */
export interface PvpConcluded {
  matchId: string;
  outcome: 'win' | 'lose' | 'draw';
}

/**
 * Why the room connection is not live.
 *
 * `failed` exists because Colyseus is WebSocket-only while the rest of the game
 * speaks Socket.io with a polling fallback (ADR-0001's "that failure mode needs
 * a real client-side error path, not a silent hang"). A player on a WS-hostile
 * network can play everything except a duel, and must be told so.
 */
export type RoomStatus = 'idle' | 'joining' | 'joined' | 'failed' | 'left';

export interface BattlegroundState {
  status: RoomStatus;
  /** The duel we are in, or were last in. Null before the first join. */
  matchId: string | null;
  /** Our own userId, read off the seat we consumed — the room state is symmetric and needs it to tell "me" from "them". */
  selfId: string | null;
  snapshot: RoomSnapshot;
  /** Set on `failed`; a player-facing sentence, not an exception message. */
  error: string | null;
  /** Non-null once the server has reported the duel's outcome. */
  result: PvpConcluded | null;
}
